import { errorCode } from '../services/api'
import { digitsOnly } from './otp'

/**
 * The sign-in page's pure pieces (auth-frame, 2026-10-14 review): the error
 * wording, the number rules and display, and the state a refresh or a Back
 * must not lose. Kept out of Auth.jsx so they are unit-testable without a
 * DOM (the frontend tests are renderToString + plain functions).
 */

/**
 * A failed request → the `auth` namespace key to show. Never the server's
 * own `message`: it is English and for developers ("The code is wrong or no
 * longer valid."), and on a Persian page its full stop jumps to the line's
 * start. Branches on the stable `error` code, as Android's FailureText does.
 */
export function authErrorKey(error, fallback = 'sendFailed') {
  // No response at all: the network or a timeout, not the server's verdict.
  if (error && !error.response) return 'offline'
  switch (errorCode(error)) {
    case 'otp_invalid':
      return 'wrongCode'
    case 'otp_throttled':
    case 'rate_limited':
      return 'throttled'
    case 'account_disabled':
      return 'disabled'
    case 'sms_unavailable':
    case 'sms_failed':
      return 'smsUnavailable'
    case 'invalid_request':
      return 'invalidPhone'
    default:
      return fallback
  }
}

/** A 429 on otp-request: a code was sent moments ago and is still good, so the right place is the code step. */
export function isThrottled(error) {
  return error?.response?.status === 429 || errorCode(error) === 'otp_throttled'
}

/** The national digits as the backend will get them: Persian/Arabic digits folded, a leading trunk 0 dropped. */
export function nationalDigits(localNumber) {
  return digitsOnly(localNumber).replace(/^0+/, '')
}

/**
 * Mobile-number shapes for the countries whose ranges Android already
 * pins (IranPhone/GermanyPhone/OmanPhone). Anything else is only held to
 * E.164 by the caller; the server is the authority there.
 */
const NATIONAL_RULES = {
  IR: /^9\d{9}$/,
  DE: /^1\d{9,10}$/,
  OM: /^\d{8}$/,
}

export function nationalNumberOk(country, localNumber) {
  const rule = NATIONAL_RULES[country?.code]
  return rule ? rule.test(nationalDigits(localNumber)) : true
}

const GROUPS = { IR: [3, 3, 4], DE: [4, 3], OM: [4, 4] }

/**
 * The number as the code step shows it: what was really sent, grouped —
 * `912 345 6789`, `1511 234 5678` — never the raw typing (a leading 0 or
 * Persian digits would be a different string from the one the SMS went to).
 * Other countries: groups of three, the last taking a remainder of up to four.
 */
export function formatNational(country, localNumber) {
  const digits = nationalDigits(localNumber)
  const sizes = GROUPS[country?.code]
  const parts = []
  let rest = digits
  if (sizes) {
    for (const size of sizes) {
      if (!rest) break
      parts.push(rest.slice(0, size))
      rest = rest.slice(size)
    }
    if (rest) parts.push(rest)
    return parts.join(' ')
  }
  while (rest.length > 4) {
    parts.push(rest.slice(0, 3))
    rest = rest.slice(3)
  }
  if (rest) parts.push(rest)
  return parts.join(' ')
}

/**
 * The sign-in state a refresh, a Back or a trip to «کد نمی‌رسد؟» must not
 * lose — the country, the number, which step, and when a resend is allowed.
 * sessionStorage: per tab, gone when the tab closes, never sent anywhere.
 * Codes live two minutes; a saved code step older than ten is stale.
 */
export const AUTH_SESSION_KEY = 'tooti.auth.signin'
const STALE_MS = 10 * 60 * 1000

export function saveAuthSession(state, storage = safeSession()) {
  if (!storage) return
  try {
    storage.setItem(AUTH_SESSION_KEY, JSON.stringify({ ...state, savedAt: Date.now() }))
  } catch {
    // Private mode or a full quota: the page simply forgets, as it did before.
  }
}

export function loadAuthSession(storage = safeSession(), now = Date.now()) {
  if (!storage) return null
  try {
    const value = JSON.parse(storage.getItem(AUTH_SESSION_KEY) ?? 'null')
    if (!value || typeof value !== 'object' || typeof value.countryCode !== 'string') return null
    if (typeof value.localNumber !== 'string') return null
    const stale = !(now - Number(value.savedAt) < STALE_MS)
    return { ...value, step: value.step === 'code' && !stale ? 'code' : 'phone' }
  } catch {
    return null
  }
}

export function clearAuthSession(storage = safeSession()) {
  try {
    storage?.removeItem(AUTH_SESSION_KEY)
  } catch {
    // Nothing to do.
  }
}

function safeSession() {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}
